/**
 * The voices a call can speak in (OpenAI text-to-speech; the backend allowlist is `VOICES` in
 * app/providers/openai/synthesize_speech.py, and the gender split matches app/services/voice/default_voice.py)
 * and which one this account uses.
 *
 * The choice lives on the user (`users.assistant_voice`): the backend picks a default when the assistant is named,
 * and Settings → Agent → Voice saves the user's own pick. It is sent as `voice` with every
 * `/agent/v2/voice/speak` request.
 */
import { computed } from 'vue'
import { setAssistantVoice } from '@/api/user.api'
import useUser from '@/composables/useUser'

export interface VoiceOption {
    id: string
    name: string
    /** A few words on how it sounds. */
    feel: string
    gender: 'male' | 'female'
}

export const VOICE_OPTIONS: VoiceOption[] = [
    { id: 'cedar', name: 'Cedar', feel: 'Warm, grounded', gender: 'male' },
    { id: 'alloy', name: 'Alloy', feel: 'Balanced, neutral', gender: 'male' },
    { id: 'ash', name: 'Ash', feel: 'Soft, steady', gender: 'male' },
    { id: 'ballad', name: 'Ballad', feel: 'Smooth, expressive', gender: 'male' },
    { id: 'echo', name: 'Echo', feel: 'Calm, even', gender: 'male' },
    { id: 'fable', name: 'Fable', feel: 'Storyteller', gender: 'male' },
    { id: 'onyx', name: 'Onyx', feel: 'Deep, authoritative', gender: 'male' },
    { id: 'verse', name: 'Verse', feel: 'Lively, versatile', gender: 'male' },
    { id: 'marin', name: 'Marin', feel: 'Natural, clear', gender: 'female' },
    { id: 'coral', name: 'Coral', feel: 'Friendly, upbeat', gender: 'female' },
    { id: 'nova', name: 'Nova', feel: 'Bright, energetic', gender: 'female' },
    { id: 'sage', name: 'Sage', feel: 'Composed, thoughtful', gender: 'female' },
    { id: 'shimmer', name: 'Shimmer', feel: 'Light, gentle', gender: 'female' },
]

export const VOICE_GROUPS: { gender: VoiceOption['gender']; title: string; voices: VoiceOption[] }[] = [
    { gender: 'male', title: 'Male voices', voices: VOICE_OPTIONS.filter((v) => v.gender === 'male') },
    { gender: 'female', title: 'Female voices', voices: VOICE_OPTIONS.filter((v) => v.gender === 'female') },
]

/** What calls use until the account has a voice (the backend's `VOICE_TTS_VOICE` default). */
export const DEFAULT_VOICE_ID = 'marin'

const known = (id: unknown): id is string => typeof id === 'string' && VOICE_OPTIONS.some((v) => v.id === id)

const { user, setUser } = useUser()

/** The voice saved on the account, or null when none has been picked yet. */
export const savedVoice = computed<string | null>(() => {
    const id = user.value?.assistant_voice
    return known(id) ? id : null
})

/** The voice to show as selected: the account's, else the default calls fall back to. */
export const selectedVoice = computed<string>(() => savedVoice.value ?? DEFAULT_VOICE_ID)

/** Save the user's pick on the account. Shows at once; resolves false (and puts the old voice back) if saving fails. */
export async function setSelectedVoice(id: string): Promise<boolean> {
    if (!known(id) || !user.value) return false
    const before = { assistant_voice: user.value.assistant_voice, assistant_voice_source: user.value.assistant_voice_source }
    setUser({ ...user.value, assistant_voice: id, assistant_voice_source: 'user' })
    const saved = await setAssistantVoice(id)
    if (saved) return true
    if (user.value) setUser({ ...user.value, ...before })
    return false
}
